import { MissingImplementationError } from '../../../errors';
import getImplementations, { Implementations } from './getImplementations';
import buildNeeds from '../utils/buildNeeds';

import { Fetch } from '../../shared/types';
import { Action, App, Need, MetadataResponse, ActionType } from '../types';

type AppAction = {
  app: App;
  action: Action;
  needs: Need[];
};

type BuildAppAction = (args: {
  implementations: Implementations;
  appSelectedApi: string;
  actionKey: string;
  actionType: ActionType;
}) => AppAction;

const buildAppAction: BuildAppAction = ({
  implementations,
  appSelectedApi,
  actionKey,
  actionType,
}) => {
  const app = implementations[appSelectedApi];

  if (!app) {
    throw new MissingImplementationError(
      `Cannot find information on app '${appSelectedApi}'.`
    );
  }

  const action = app.actions.find(
    ({ key, type }) => key === actionKey && type === actionType
  );

  if (!action) {
    throw new MissingImplementationError(
      `Cannot find information on action '${actionKey}' for app '${appSelectedApi}'.`
    );
  }

  return {
    app: {
      selected_api: app.selected_api,
      auth_type: app.auth_type,
      name: app.name,
      images: app.images,
      primary_color: app.primary_color,
      actions: app.actions,
    },
    action: {
      type: action.type,
      key: action.key,
      name: action.name,
      noun: action.noun,
      meta: action.meta,
    },
    needs: buildNeeds(action.needs, app.selected_api, action.key, app.actions),
  };
};

type MetadataQuery = {
  readAppKey: string;
  readActionKey: string;
  readActionType: ActionType;
  writeAppKey: string;
  writeActionKey: string;
  writeActionType: ActionType;
};

export const getMetadata = async (
  fetch: Fetch,
  query: MetadataQuery
): Promise<MetadataResponse> => {
  const {
    readAppKey,
    readActionKey,
    readActionType,
    writeAppKey,
    writeActionKey,
    writeActionType,
  } = query;

  const implementations = await getImplementations(
    fetch,
    readAppKey,
    writeAppKey
  );

  const read = buildAppAction({
    implementations,
    appSelectedApi: readAppKey,
    actionKey: readActionKey,
    actionType: readActionType,
  });

  const write = buildAppAction({
    implementations,
    appSelectedApi: writeAppKey,
    actionKey: writeActionKey,
    actionType: writeActionType,
  });

  return {
    meta: {
      read: {
        app: read.app,
        action: read.action,
      },
      write: {
        app: write.app,
        action: write.action,
      },
    },
    readNeeds: read.needs,
    writeNeeds: write.needs,
  };
};

export default getMetadata;
